/**
 * Timezone utilities for consistent Eastern Time handling across the application
 */

// Eastern Time timezone identifier
export const EASTERN_TIMEZONE = 'America/New_York';

/**
 * Convert a date string or Date object to Eastern Time
 * @param {string|Date} dateInput - The date to convert
 * @returns {Date} - Date object in Eastern Time
 */
export const toEasternTime = (dateInput) => {
  if (!dateInput) return null;
  
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return null;
  
  // Convert to Eastern Time using Intl.DateTimeFormat
  const easternDate = new Date(date.toLocaleString("en-US", { timeZone: EASTERN_TIMEZONE }));
  return easternDate;
};

/**
 * Format a date string to Eastern Time date string (YYYY-MM-DD)
 * @param {string|Date} dateInput - The date to format
 * @returns {string} - Formatted date string in YYYY-MM-DD format
 */
export const formatDateToEastern = (dateInput) => {
  if (!dateInput) return '';
  
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '';
  
  return date.toLocaleDateString('en-CA', { 
    timeZone: EASTERN_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/**
 * Format a date string to Eastern Time for display (MMM DD, YYYY)
 * @param {string|Date} dateInput - The date to format
 * @returns {string} - Formatted date string for display
 */
export const formatDateDisplay = (dateInput) => {
  if (!dateInput) return 'N/A';
  
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return 'N/A';
  
  return date.toLocaleDateString('en-US', {
    timeZone: EASTERN_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format a date string to Eastern Time with time (MMM DD, YYYY HH:MM AM/PM)
 * @param {string|Date} dateInput - The date to format
 * @returns {string} - Formatted date and time string for display
 */
export const formatDateTimeDisplay = (dateInput) => {
  if (!dateInput) return 'N/A';
  
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return 'N/A';
  
  return date.toLocaleString('en-US', {
    timeZone: EASTERN_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Get today's date in Eastern Time (YYYY-MM-DD)
 * @returns {string} - Today's date in YYYY-MM-DD format
 */
export const getTodayEastern = () => {
  const now = new Date();
  return now.toLocaleDateString('en-CA', { 
    timeZone: EASTERN_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/**
 * Get tomorrow's date in Eastern Time (YYYY-MM-DD)
 * @returns {string} - Tomorrow's date in YYYY-MM-DD format
 */
export const getTomorrowEastern = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toLocaleDateString('en-CA', { 
    timeZone: EASTERN_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/**
 * Format date and time for API requests (ISO string in Eastern Time)
 * @param {string|Date} dateInput - The date to format
 * @returns {string} - ISO string in Eastern Time
 */
export const formatDateForAPI = (dateInput) => {
  if (!dateInput) return '';
  
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '';
  
  // Convert to Eastern Time and return ISO string
  const easternDate = new Date(date.toLocaleString("en-US", { timeZone: EASTERN_TIMEZONE }));
  return easternDate.toISOString();
};

/**
 * Get relative time string (e.g., "2h ago", "3d ago") in Eastern Time
 * @param {string|Date} dateInput - The date to compare
 * @returns {string} - Relative time string
 */
export const getRelativeTime = (dateInput) => {
  if (!dateInput) return 'N/A';
  
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return 'N/A';
  
  const now = new Date();
  const diffInMinutes = Math.floor((now - date) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
  if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
  
  return formatDateDisplay(dateInput);
};

/**
 * Check if a date is today in Eastern Time
 * @param {string|Date} dateInput - The date to check
 * @returns {boolean} - True if the date is today
 */
export const isTodayEastern = (dateInput) => {
  if (!dateInput) return false;
  
  const today = getTodayEastern();
  const dateStr = formatDateToEastern(dateInput);
  return today === dateStr;
};

/**
 * Check if a date is tomorrow in Eastern Time
 * @param {string|Date} dateInput - The date to check
 * @returns {boolean} - True if the date is tomorrow
 */
export const isTomorrowEastern = (dateInput) => {
  if (!dateInput) return false;
  
  const tomorrow = getTomorrowEastern();
  const dateStr = formatDateToEastern(dateInput);
  return tomorrow === dateStr;
};

/**
 * Get date range for different time frames in Eastern Time
 * @param {string} timeFrame - The time frame ('daily', 'weekly', 'monthly', 'yearly')
 * @returns {Object} - Object with start and end dates in YYYY-MM-DD format
 */
export const getDateRangeEastern = (timeFrame) => {
  const end = new Date();
  const start = new Date();
  
  switch (timeFrame) {
    case 'daily':
      start.setDate(end.getDate() - 30); // Last 30 days
      break;
    case 'weekly':
      start.setDate(end.getDate() - 90); // Last 90 days
      break;
    case 'monthly':
      start.setMonth(end.getMonth() - 12); // Last 12 months
      break;
    case 'yearly':
      start.setFullYear(end.getFullYear() - 5); // Last 5 years
      break;
    default:
      start.setDate(end.getDate() - 30);
  }
  
  return {
    start: formatDateToEastern(start),
    end: formatDateToEastern(end)
  };
};
